import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import {
    samConstants,
    samDayKey,
    SAM_EMPTY_CONVERSATION,
} from "@data/constants/samConstants"
import { authService } from "@data/services/authService"
import { samService } from "@data/services/samService"

/**
 * Sam slice — both contextual data (per activity) and live conversation
 * state (per activity). The conversation here is what the sheet shows; the
 * one Sam reads is kept by the server, which never takes it from the app.
 *
 * Shape:
 *   {
 *     contextByActivityId: { [id]: { source_kind, activity, subject, tutor, occurrence, sequence, prescribed_by, fetchedAt } },
 *     conversationByActivityId: {
 *       [id]: {
 *         messages: [{ id, role, content, mood?, createdAt, origin? }],
 *         isThinking: boolean,
 *         conversationUuid: string | null,
 *         capReached: boolean,
 *         degraded: boolean,
 *         lastMood: string,
 *       }
 *     },
 *     proactiveAcksByActivityId: {
 *       [id]: { teaser_t5: bool, idle_t3min: bool }
 *     },
 *     dailyCapReachedOn: string | null,   // samDayKey() du jour où le plafond du jour a été atteint
 *   }
 */
const initialState = {
    contextByActivityId: {},
    contextErrorByActivityId: {},
    conversationByActivityId: {},
    proactiveAcksByActivityId: {},
    dailyCapReachedOn: null,
}

function ensureConversation(state, activityId) {
    const map = state.conversationByActivityId || {}
    return map[activityId] || SAM_EMPTY_CONVERSATION
}

/**
 * Écrit un correctif sur l'entrée d'UNE activité, en préservant le reste.
 * Chaque cas ci-dessous répétait sinon le même triple étalement — trois
 * niveaux d'imbrication où une faute de frappe écrase silencieusement les
 * autres activités.
 */
function patchConversation(state, activityId, patch) {
    return {
        ...state,
        conversationByActivityId: {
            ...(state.conversationByActivityId || {}),
            [activityId]: { ...ensureConversation(state, activityId), ...patch },
        },
    }
}

export function sam(state = initialState, action) {
    switch (action.type) {
        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authService.logout.name + apiConstants._SUCCESS:
            return initialState

        case samService.getSamContext.name + apiConstants._SUCCESS: {
            // La conversation gardée n'est pas du contexte : elle va dans la
            // feuille, pas dans `contextByActivityId` (ni dans la persistance).
            const { conversation: kept = [], ...ctx } = action.data || {}
            const activityId = ctx?.activity?.id
            if (!activityId) return state
            // Clear any prior context error for this activity — a fresh
            // success means we can render the Sam chrome again.
            const clearedErrors = { ...(state.contextErrorByActivityId || {}) }
            delete clearedErrors[activityId]
            const conversation = ensureConversation(state, activityId)
            // La conversation que garde le serveur — celle que Sam relit —
            // reprend sa place quand l'élève n'a rien écrit ici : l'app ne
            // garde que ses dernières activités, ou il a changé d'appareil.
            const hydrated =
                kept.length > 0 &&
                !conversation.messages.some((m) => m.role === "student")
                    ? kept.map((m, i) => ({
                          id: `kept-${activityId}-${i}`,
                          role: m.role,
                          content: m.content,
                          createdAt: new Date().toISOString(),
                      }))
                    : null
            const next = hydrated
                ? patchConversation(state, activityId, { messages: hydrated })
                : state
            return {
                ...next,
                contextByActivityId: {
                    ...state.contextByActivityId,
                    [activityId]: {
                        ...ctx,
                        fetchedAt: Date.now(),
                    },
                },
                contextErrorByActivityId: clearedErrors,
            }
        }

        case samService.getSamContext.name + apiConstants._FAILURE: {
            // Sam context fetch failed (404/500). Flag the error per
            // activity so the screen can degrade to the standard activity
            // flow instead of hanging on an infinite spinner. Defensive:
            // coerce the map since redux-persist may hydrate a pre-flag
            // sam slice where `contextErrorByActivityId` doesn't exist.
            const activityId = action?.routeParams?.id
            if (!activityId) return state
            return {
                ...state,
                contextErrorByActivityId: {
                    ...(state.contextErrorByActivityId || {}),
                    [activityId]: true,
                },
            }
        }

        case samService.sendSamMessage.name + apiConstants._REQUEST: {
            const activityId = action?.routeParams?.activity_id
            if (!activityId) return state
            const conv = ensureConversation(state, activityId)
            const optimisticStudentMessage = {
                id: `local-${Date.now()}`,
                role: "student",
                content: action?.data?.message,
                origin: action?.data?.message_origin || "text",
                createdAt: new Date().toISOString(),
            }
            return patchConversation(state, activityId, {
                messages: [...conv.messages, optimisticStudentMessage],
                isThinking: true,
            })
        }

        case samService.sendSamMessage.name + apiConstants._SUCCESS: {
            const activityId = action?.routeParams?.activity_id
            const reply = action.data
            if (!activityId || !reply) return state
            const conv = ensureConversation(state, activityId)
            const samMessage = {
                id: `sam-${Date.now()}`,
                role: "sam",
                content: reply.reply,
                mood: reply.mood,
                createdAt: new Date().toISOString(),
            }
            return patchConversation(state, activityId, {
                isThinking: false,
                messages: [...conv.messages, samMessage],
                conversationUuid: reply.conversation_uuid || conv.conversationUuid,
                degraded: !!reply.degraded,
                capReached: !!reply.cap_reached,
                lastMood: reply.mood || conv.lastMood,
            })
        }

        case samService.sendSamMessage.name + apiConstants._FAILURE: {
            const activityId = action?.routeParams?.activity_id
            if (!activityId) return state
            const conv = ensureConversation(state, activityId)

            // Un cap atteint arrive en 422 — donc par le chemin d'échec — mais
            // ce n'est PAS une panne : le serveur renvoie déjà la phrase à
            // afficher et `cap_reached`. Le traiter comme un échec générique
            // faisait apparaître « Sam met du temps à répondre » et perdait sa
            // réponse : l'élève voyait ses messages s'empiler sans un mot.
            const payload = action?.error?.data
            if (action?.error?.error === "cap_reached" || payload?.cap_reached) {
                const reply = payload?.reply
                // Le plafond du jour ferme Sam sur toutes les activités jusqu'à
                // demain : il se garde à part, pas sur ce seul fil — sinon
                // l'activité suivante laissait écrire, pour un nouveau refus.
                const daily = payload?.cap_scope === "daily"
                const next = patchConversation(state, activityId, {
                    isThinking: false,
                    degraded: false,
                    capReached: !daily,
                    messages: reply
                        ? [
                              ...conv.messages,
                              {
                                  id: `sam-cap-${Date.now()}`,
                                  role: "sam",
                                  content: reply,
                                  mood: payload?.mood || "patient",
                                  createdAt: new Date().toISOString(),
                              },
                          ]
                        : conv.messages,
                })
                return daily ? { ...next, dailyCapReachedOn: samDayKey() } : next
            }

            // Tout n'est pas une panne. Un refus explicite du serveur — « tu
            // vas trop vite », « ton message est trop long » — doit être DIT à
            // l'élève, pas déguisé en « Sam met du temps à répondre », qui le
            // laisse devant un message sans réponse et sans explication.
            const status = action?.error?.status
            const refus =
                status === 429
                    ? "Tu vas un peu vite pour moi ! Laisse-moi le temps de souffler et réessaie dans un instant."
                    : status === 422 && action?.error?.errors
                      ? "Ton message est trop long pour moi. Tu peux le raccourcir ?"
                      : null

            if (refus) {
                return patchConversation(state, activityId, {
                    isThinking: false,
                    degraded: false,
                    messages: [
                        ...conv.messages,
                        {
                            id: `sam-refus-${Date.now()}`,
                            role: "sam",
                            content: refus,
                            mood: "patient",
                            createdAt: new Date().toISOString(),
                        },
                    ],
                })
            }

            return patchConversation(state, activityId, {
                isThinking: false,
                degraded: true,
            })
        }

        case samConstants.DROP_SAM_MESSAGE: {
            const { activityId, messageId } = action
            const conv = state.conversationByActivityId?.[activityId]
            if (!conv || !conv.messages?.some((m) => m.id === messageId)) return state
            return patchConversation(state, activityId, {
                messages: conv.messages.filter((m) => m.id !== messageId),
                degraded: false,
            })
        }

        case samConstants.RESET_SAM_CONVERSATION: {
            const { activityId } = action
            if (!activityId) return state
            const nextConv = { ...state.conversationByActivityId }
            const nextAcks = { ...state.proactiveAcksByActivityId }
            delete nextConv[activityId]
            delete nextAcks[activityId]
            return {
                ...state,
                conversationByActivityId: nextConv,
                proactiveAcksByActivityId: nextAcks,
            }
        }

        case samConstants.INJECT_STUDENT_SCAN: {
            const { activityId, imageUri } = action
            if (!activityId || !imageUri) return state
            const conv = ensureConversation(state, activityId)
            const scanMessage = {
                id: `student-scan-${Date.now()}`,
                role: "student",
                kind: "scan",
                // La bulle n'affiche que l'image ; côté serveur, la photo entre
                // dans la conversation que Sam relit.
                imageUri,
                createdAt: new Date().toISOString(),
            }
            return patchConversation(state, activityId, {
                messages: [...conv.messages, scanMessage],
            })
        }

        case samConstants.ACK_SAM_PROACTIVE_BUBBLE: {
            const { activityId, key } = action
            if (!activityId || !key) return state
            // Defensive: redux-persist may hydrate a pre-D-2 sam slice
            // where `proactiveAcksByActivityId` doesn't exist yet, so we
            // always coerce the map before reading/writing.
            const acksMap = state.proactiveAcksByActivityId || {}
            const prev = acksMap[activityId] || {}
            if (prev[key]) return state
            return {
                ...state,
                proactiveAcksByActivityId: {
                    ...acksMap,
                    [activityId]: { ...prev, [key]: true },
                },
            }
        }

        case samConstants.INJECT_SAM_BUBBLE: {
            const { activityId, content, mood, origin } = action
            if (!activityId || !content) return state
            const conv = ensureConversation(state, activityId)
            const samMessage = {
                id: `sam-inject-${Date.now()}`,
                role: "sam",
                content,
                mood: mood || conv.lastMood,
                // Cf. injectSamBubble : "proactive" = simple bonjour, donc
                // l'écran d'accueil de la feuille peut rester affiché.
                ...(origin ? { origin } : {}),
                createdAt: new Date().toISOString(),
            }
            return patchConversation(state, activityId, {
                messages: [...conv.messages, samMessage],
                lastMood: mood || conv.lastMood,
            })
        }

        default:
            return state
    }
}
